<template>
  <div
    class="addon gameflow"
    :class="{
      'gameflow--is-disabled':
        (editingState.activeTabCategory === SectionType.SECTION && campaignState.isEditModeActive) ||
        (editingState.activeTabCategory === SectionType.POPOVER && campaignState.isEditModeActive)
    }"
  >
    <Flow :ejected="false">
      <div
        v-if="flowModel && campaignState.isPopup"
        :key="flowModel?.state?.id"
        :class="[
          'positioner',
          `positioner--position-${(flowModel?.state.config.settings.state?.basic?.position?.type ?? '').replace(
            '_',
            '-'
          )}`
        ]"
      >
        <LFSection :model="flowModel" :section-order="flowModel?.index" />
      </div>

      <template v-else-if="flowModel">
        <LFSection :key="flowModel?.state.id" :model="flowModel" :section-order="flowModel?.index" />
      </template>
    </Flow>

    <div v-if="globalCampaignState.flowOverlay" class="flow__overlay"></div>
    <ControlCode
      v-if="controlCodeSettings.enabled"
      :settings="controlCodeSettings"
      @onClose="isControlCodeClosed = true"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, computed, ref, watch, nextTick } from 'vue';
import { AddonGameflowModel } from '@/components/addons/gameflow/Model';
import { useStore } from '@/store';
import ControlCode, { ControlCodeSettings } from '@/components/components/ControlCode.vue';
import { SectionType } from '@/typings/enums/enums';
import Flow from '@/components/Flow/Flow.vue';
import { State } from '@/store/modules/campaign/state';
import { FlowpageModel } from '@/components/layout/FlowpageModel';
import {
  ControlCodeTypes,
  IntegrationBulkPrizeVouchersModel
} from '@/components/integrations/bulk-prize-vouchers/Model';
import { generateUniqueId } from '@/utilities/Utilities';
import { scrollFlowIntoFocus } from '@/utilities/Flow';
import useFlow from '@/hooks/useFlow';

export default defineComponent({
  name: 'AddonGameflow',
  components: {
    Flow,
    ControlCode
  },
  inheritAttrs: false,
  props: {
    model: {
      type: Object as PropType<AddonGameflowModel>,
      required: true
    }
  },
  setup() {
    const store = useStore();
    const campaignModel = store.getters['campaign/model'];
    const editingState = store.state.editing;
    const flowModel = computed<FlowpageModel | undefined>(() => {
      if (store.getters['campaign/fictiveFlowPage']) {
        return store.getters['campaign/fictiveFlowPage'];
      }
      const flowId = (store.state.campaign as State).flowId;

      if (flowId) {
        return campaignModel.getFlowPageModel(flowId);
      }

      return undefined;
    });

    const controlPrizeCode = computed(() => {
      const code = store.getters['global/replacementTags']['control_code']
        ? store.getters['global/replacementTags']['control_code']
        : store.getters['global/replacementTags']['bulk_prize_control_code'];

      if (code === '' || typeof code === 'undefined') {
        return;
      }

      return String(code).toUpperCase();
    });
    const instantWinControlCode = store.getters['campaign/state'].advanced.controlCode;

    const isControlCodeClosed = ref(false);

    const isWinner = computed(() => {
      return !!store.getters['global/isGameWinner'];
    });

    const isControlCodeOpen = computed(() => {
      // looking at is winner allow us to only transition when game is actually ended and not when registration is set as winner.

      if (controlCodeData.value?.type === ControlCodeTypes.REPLACEMENT_TAG) {
        return false;
      }

      // registration response adds replacement tags for bulk prizes. If its exits we add it to the page.
      return isWinner.value && !isControlCodeClosed.value;
    });

    const controlCodeData = computed(() => {
      const bulkControlCodeModel = store.state.campaign.model?.state.config?.integrations.find(
        (integration) => integration.state.namespace === 'bulk_prize_vouchers'
      ) as IntegrationBulkPrizeVouchersModel; // not sure if there is any way to type this as its this map of IntegrationModel

      // if bulk prizes control code
      if (
        store.getters['global/replacementTags']['bulk_prize_control_code'] &&
        bulkControlCodeModel.state?.controlCode?.enabled
      ) {
        return bulkControlCodeModel.state.controlCode;
      }

      // if instant win control code
      if (instantWinControlCode.enabled && store.getters['global/replacementTags']['control_code']) {
        return instantWinControlCode;
      }

      return undefined;
    });

    const constrolCodeLabel = computed(() => {
      return controlPrizeCode.value ? controlCodeData.value?.label?.replace('@code', controlPrizeCode.value) : '';
    });

    const controlCodeSettings = computed<ControlCodeSettings>(() => {
      return { ...controlCodeData.value, active: isControlCodeOpen.value, label: constrolCodeLabel.value };
    });

    const setCurrentFlowPageClass = (index: number) => {
      /**
       * I dont have a good explanation on the 20, but that is how the old platform does it
       */
      for (let i = 0; i <= 20; i++) {
        store.dispatch('utility/removeBodyClass', `current-flow-page-${i}`);
      }
      store.dispatch('utility/addBodyClass', `current-flow-page-${index + 1}`);
    };

    watch(
      () => store.getters['campaign/flowIndex'],
      async () => {
        const firstFlowPage = useFlow().getFirstFlowPage();

        if (firstFlowPage !== store.getters['campaign/flowId']) {
          scrollFlowIntoFocus();
        }

        await nextTick();
        const currentFlowIndex = store.getters['campaign/flowIndex'] ?? 0;
        setCurrentFlowPageClass(currentFlowIndex);
      },
      {
        immediate: true
      }
    );

    return {
      flowModel,
      SectionType,
      editingState,
      campaignState: campaignModel.state,
      globalCampaignState: store.state.campaign,
      controlCodeSettings,
      isControlCodeClosed,
      generateUniqueId
    };
  }
});
</script>

<style lang="scss">
.gameflow {
  &--is-disabled {
    pointer-events: none;
  }
}

.flow {
  position: relative;
  z-index: 1;

  &--transitioning {
    transition: height 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }

  &__overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #000;
    opacity: 0.7;
    z-index: 0;
    transition: opacity 400ms linear;
  }
}
</style>
